import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';

export const LONG_FORM_CONTENT_TYPE = {
  webpage: 'web_page',
  blogPost: 'blog_post',
  email: 'email',
  instagramPost: 'instagram_caption',
  facebookPost: 'facebook_post',
  linkedinPost: 'linkedin_post',
  twitterPost: 'twitter_post',
  facebookAd: 'facebook_ad',
  linkedinAd: 'linkedin_ad',
  linkedinConversationAd: 'linkedin_conversation_ad',
  linkedinMessageAd: 'linkedin_message_ad',
  linkedinLeadGenFormAd: 'linkedin_lead_gen_form_ad',
  linkedinDocumentAd: 'linkedin_document_ad',
  linkedinTextAd: 'linkedin_text_ad',
  linkedinVideoAd: 'linkedin_video_ad',
  twitterAd: 'twitter_ad',
  googlePerformanceMaxAd: 'google_performance_max_ad',
  googleSearchAd: 'google_search_ad',
  pinterestAd: 'pinterest_ad',
  whitePaper: 'white_paper',
  researchReport: 'research_report',
  image: 'image',
  generalText: 'general_text',
} as const;
export type LongFormContentType =
  (typeof LONG_FORM_CONTENT_TYPE)[keyof typeof LONG_FORM_CONTENT_TYPE];

interface LongFormContentTypeProps {
  title: string;
  generationTool?: GenerationTool;
}

const LONG_FORM_CONTENT_TYPE_PROPS: Record<LongFormContentType, LongFormContentTypeProps> = {
  [LONG_FORM_CONTENT_TYPE.webpage]: {
    title: 'Webpage',
    generationTool: GENERATION_TOOL.basicLandingPage, // TODO: use some non-deprecated tool
  },
  [LONG_FORM_CONTENT_TYPE.blogPost]: {
    title: 'Blog / Article',
    generationTool: GENERATION_TOOL.blogDescription,
  },
  [LONG_FORM_CONTENT_TYPE.email]: {
    title: 'Email',
    generationTool: GENERATION_TOOL.emailMixture,
  },
  [LONG_FORM_CONTENT_TYPE.instagramPost]: {
    title: 'Instagram post',
    generationTool: GENERATION_TOOL.instagramCaption,
  },
  [LONG_FORM_CONTENT_TYPE.facebookPost]: {
    title: 'Facebook post',
    generationTool: GENERATION_TOOL.facebookPost,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinPost]: {
    title: 'LinkedIn post',
    generationTool: GENERATION_TOOL.linkedinPost,
  },
  [LONG_FORM_CONTENT_TYPE.twitterPost]: {
    title: 'X post',
    generationTool: GENERATION_TOOL.tweet,
  },
  [LONG_FORM_CONTENT_TYPE.facebookAd]: {
    title: 'Meta Image ad',
    generationTool: GENERATION_TOOL.facebookMixtureAd,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinAd]: {
    title: 'LinkedIn Single Image ad',
    generationTool: GENERATION_TOOL.linkedinMixtureAd,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinConversationAd]: {
    title: 'LinkedIn Conversation ad',
    generationTool: GENERATION_TOOL.linkedinConversationAd,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinMessageAd]: {
    title: 'LinkedIn Message ad',
    generationTool: GENERATION_TOOL.linkedinMessageAd,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinLeadGenFormAd]: {
    title: 'LinkedIn Lead Gen Form ad',
    generationTool: GENERATION_TOOL.linkedinLeadGenFormAd,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinDocumentAd]: {
    title: 'LinkedIn Document ad',
    generationTool: GENERATION_TOOL.linkedinDocumentAd,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinTextAd]: {
    title: 'LinkedIn Text ad',
    generationTool: GENERATION_TOOL.linkedinTextAd,
  },
  [LONG_FORM_CONTENT_TYPE.linkedinVideoAd]: {
    title: 'LinkedIn Video ad',
    generationTool: GENERATION_TOOL.linkedinVideoAd,
  },
  [LONG_FORM_CONTENT_TYPE.twitterAd]: {
    title: 'X Image ad',
    generationTool: GENERATION_TOOL.twitterMixtureAd,
  },
  [LONG_FORM_CONTENT_TYPE.googlePerformanceMaxAd]: {
    title: 'Google Performance Max ad',
    generationTool: GENERATION_TOOL.googlePerformanceMaxAd,
  },
  [LONG_FORM_CONTENT_TYPE.googleSearchAd]: {
    title: 'Google Search ad',
    generationTool: GENERATION_TOOL.googleRSA,
  },
  [LONG_FORM_CONTENT_TYPE.pinterestAd]: {
    title: 'Pinterest Image ad',
    generationTool: GENERATION_TOOL.pinterestMixtureAd,
  },
  [LONG_FORM_CONTENT_TYPE.whitePaper]: {
    title: 'White paper',
    generationTool: GENERATION_TOOL.basicPerfectHeadline,
  },
  [LONG_FORM_CONTENT_TYPE.researchReport]: {
    title: 'Research report',
    generationTool: GENERATION_TOOL.basicPerfectHeadline,
  },
  [LONG_FORM_CONTENT_TYPE.image]: {
    title: 'General image',
    generationTool: GENERATION_TOOL.imageByStyle,
  },
  [LONG_FORM_CONTENT_TYPE.generalText]: {
    title: 'Other',
    generationTool: GENERATION_TOOL.basicContentImprover,
  },
};

export const getLongFormContentTypeTitle = (contentType: LongFormContentType): string =>
  LONG_FORM_CONTENT_TYPE_PROPS[contentType]?.title || contentType;

export const getLongFormContentTypeTool = (
  contentType: LongFormContentType
): GenerationTool | undefined => {
  return LONG_FORM_CONTENT_TYPE_PROPS[contentType]?.generationTool;
};
